import * as React from "react";
import Link from "next/link";
import { Sparkles, ArrowRight, ShieldCheck } from "lucide-react";

interface DemoCommercialBannerProps {
  badge?: string;
  title: string;
  description: string;
  targetUrl: string;
  targetLabel: string;
  secondaryUrl?: string;
  secondaryLabel?: string;
  accent?: "orange" | "amber" | "rose" | "blue" | "emerald";
}

const colorMap = {
  orange: {
    border: "border-orange-500/30",
    bg: "from-orange-950/40 via-stone-900 to-orange-950/40",
    badgeBg: "bg-orange-500/10 border-orange-500/30 text-orange-400",
    btnPrimary: "bg-orange-600 hover:bg-orange-500 text-white shadow-orange-600/20",
    btnSecondary: "bg-stone-800/80 hover:bg-stone-700 text-stone-200 border-stone-700",
    accentText: "text-orange-400",
  },
  amber: {
    border: "border-amber-500/30",
    bg: "from-amber-950/40 via-stone-900 to-amber-950/40",
    badgeBg: "bg-amber-500/10 border-amber-500/30 text-amber-400",
    btnPrimary: "bg-amber-600 hover:bg-amber-500 text-white shadow-amber-600/20",
    btnSecondary: "bg-stone-800/80 hover:bg-stone-700 text-stone-200 border-stone-700",
    accentText: "text-amber-400",
  },
  rose: {
    border: "border-rose-500/30",
    bg: "from-rose-950/40 via-stone-900 to-rose-950/40",
    badgeBg: "bg-rose-500/10 border-rose-500/30 text-rose-400",
    btnPrimary: "bg-rose-600 hover:bg-rose-500 text-white shadow-rose-600/20",
    btnSecondary: "bg-stone-800/80 hover:bg-stone-700 text-stone-200 border-stone-700",
    accentText: "text-rose-400",
  },
  blue: {
    border: "border-blue-500/30",
    bg: "from-blue-950/40 via-stone-900 to-blue-950/40",
    badgeBg: "bg-blue-500/10 border-blue-500/30 text-blue-400",
    btnPrimary: "bg-blue-600 hover:bg-blue-500 text-white shadow-blue-600/20",
    btnSecondary: "bg-stone-800/80 hover:bg-stone-700 text-stone-200 border-stone-700",
    accentText: "text-blue-400",
  },
  emerald: {
    border: "border-emerald-500/30",
    bg: "from-emerald-950/40 via-stone-900 to-emerald-950/40",
    badgeBg: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
    btnPrimary: "bg-emerald-600 hover:bg-emerald-500 text-white shadow-emerald-600/20",
    btnSecondary: "bg-stone-800/80 hover:bg-stone-700 text-stone-200 border-stone-700",
    accentText: "text-emerald-400",
  },
};

export const DemoCommercialBanner: React.FC<DemoCommercialBannerProps> = ({
  badge = "Live Interactive Demonstration Sandbox",
  title,
  description,
  targetUrl,
  targetLabel,
  secondaryUrl,
  secondaryLabel,
  accent = "orange",
}) => {
  const c = colorMap[accent];

  return (
    <div className={`w-full bg-gradient-to-r ${c.bg} border-y ${c.border} py-6 px-4 sm:px-6 my-6 relative overflow-hidden`}>
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
        <div className="space-y-1.5 text-center md:text-left max-w-3xl">
          <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
            <span className={`inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full border text-[11px] font-bold uppercase tracking-wider ${c.badgeBg}`}>
              <Sparkles className="w-3.5 h-3.5" />
              <span>{badge}</span>
            </span>
            <span className="text-stone-400 text-xs flex items-center gap-1">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Zero Monthly SaaS Fees
            </span>
          </div>

          <h3 className="text-lg sm:text-xl font-heading font-bold text-white tracking-tight">
            {title}
          </h3>

          <p className="text-xs sm:text-sm text-stone-300 leading-relaxed">
            {description}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
          <Link
            href={targetUrl}
            className={`px-5 py-2.5 rounded-xl font-heading font-bold text-xs shadow-lg flex items-center gap-2 transition-all hover:scale-105 ${c.btnPrimary}`}
          >
            <span>{targetLabel}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>

          {secondaryUrl && secondaryLabel && (
            <Link
              href={secondaryUrl}
              className={`px-4 py-2.5 rounded-xl font-heading font-semibold text-xs border transition-colors ${c.btnSecondary}`}
            >
              {secondaryLabel}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
